import {SLQueryParamConfig} from '#/contexts/query-params-provider';
import {useEffect} from 'react';
import {useQueryParams} from 'use-query-params';

type TabKeysValue = Record<string, string>;

export const useQueryParamsTab = <TabKeys extends TabKeysValue>(params: {
  queryParamTabKey: keyof SLQueryParamConfig;
  tabKeys: TabKeys;
  initialTabKey?: keyof TabKeys;
}): {
  tabIndex: number;
  setTabIndex: (tabIndex: number) => void;
} => {
  // Props
  const {queryParamTabKey, tabKeys, initialTabKey = getTabKeyFromTabIndex(0, tabKeys)} = params;

  // External hooks
  const [queryParams, setQueryParams] = useQueryParams<SLQueryParamConfig>();

  // Vars
  const currentTabKey = queryParams[queryParamTabKey] as keyof TabKeys;
  const tabIndex = getTabIndexFromTabKey<TabKeys>(currentTabKey, tabKeys);

  // Functions
  const setTabIndex = (newTabIndex: number) => {
    const tabKey = getTabKeyFromTabIndex<TabKeys>(newTabIndex, tabKeys);
    setQueryParams({[queryParamTabKey]: tabKey}, 'replaceIn');
  };

  // Effects
  useEffect(() => {
    if (currentTabKey === undefined) {
      setQueryParams({[queryParamTabKey]: initialTabKey}, 'replaceIn');
    }
  }, [currentTabKey, queryParamTabKey, initialTabKey, setQueryParams]);

  return {tabIndex, setTabIndex};
};

const getTabKeyFromTabIndex = <TabKeys extends TabKeysValue>(tabIndex: number, tabs: TabKeys): keyof TabKeys =>
  Object.values(tabs)[tabIndex];

const getTabIndexFromTabKey = <TabKeys extends TabKeysValue>(tabKey: keyof TabKeys, tabs: TabKeys): number => {
  const tabIndex = Object.values(tabs).findIndex(elem => elem === tabKey);
  return tabIndex === -1 ? 0 : tabIndex;
};

export const useQueryParamsTabKey = <TabKeys extends TabKeysValue>(params: {
  queryParamTabKey: keyof SLQueryParamConfig;
  tabKeys: TabKeys;
  initialTabKey?: TabKeys[keyof TabKeys];
}): {
  tab: keyof TabKeys;
  setTab: (tabKey: TabKeys[keyof TabKeys]) => void;
} => {
  // Props
  const {queryParamTabKey, tabKeys, initialTabKey = Object.values(tabKeys)[0]} = params;

  // External hooks
  const [queryParams, setQueryParams] = useQueryParams<SLQueryParamConfig>();

  // Vars
  const tab = queryParams[queryParamTabKey] as keyof TabKeys;

  // Functions
  const setTab = (newTabKey: TabKeys[keyof TabKeys]) => {
    setQueryParams({[queryParamTabKey]: newTabKey}, 'replaceIn');
  };

  // Effects
  useEffect(() => {
    if (tab === undefined) {
      setQueryParams({[queryParamTabKey]: initialTabKey}, 'replaceIn');
    }
  }, [tab, queryParamTabKey, initialTabKey, setQueryParams]);

  return {tab, setTab};
};
